/**
 * Canonical site-level constants used by every SEO surface (meta tags, JSON-LD,
 * sitemap). Kept free of React so the build-time prerender/sitemap script can
 * import the same values without pulling in the app.
 */

/**
 * Absolute origin of the public site, with no trailing slash.
 * Overridable per-environment so preview deploys don't emit production
 * canonicals (which would point Google at the wrong host).
 */
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://www.storiesvillage.com').replace(
	/\/+$/,
	'',
);

export const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'StoriesVillage';

export const DEFAULT_DESCRIPTION =
	process.env.REACT_APP_META_DESC ||
	"StoriesVillage creates personalized, illustrated children's books starring your child as the hero of their own story — hand-crafted keepsakes, printed and shipped to your door.";

/** Fallback social-share image. Must be an absolute URL for OG/Twitter to resolve it. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/hero/kid-1/watercolor_scene.png`;

export const TWITTER_HANDLE = '@storiesvillage';

export const SUPPORT_EMAIL = 'support@storiesvillage.com';

/**
 * Official social profiles. Single source of truth for the footer icons and the
 * Organization `sameAs` array, which is how Google links this site to those
 * profiles in its knowledge graph.
 *
 * Only add a URL here once the profile actually exists — pointing `sameAs` at a
 * bare domain (https://tiktok.com) is an unverifiable claim, and a footer link
 * to a network's home page is a dead end for users.
 */
export const SOCIAL_LINKS: { name: string; url: string }[] = [
	{ name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61592021132738' },
	{ name: 'Instagram', url: 'https://www.instagram.com/storiesvillage2026/' },
	// TODO: add TikTok and LinkedIn here once those profiles are live.
];

/** Build an absolute URL from a site-relative path. Absolute inputs pass through. */
export const absoluteUrl = (path?: string): string => {
	if (!path) return SITE_URL;
	if (/^https?:\/\//i.test(path)) return path;
	return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Strip query/hash and any trailing slash so a page has exactly one canonical
 * form. The prerenderer visits routes with `?lng=en`, which must never leak
 * into the emitted canonical tag.
 */
export const canonicalFor = (pathname: string): string => {
	const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '');
	return `${SITE_URL}${clean || '/'}`;
};
